import { useTheme } from '@core/hooks/useTheme';
import { logger } from '@core/logging';
import { generateCorrelationId } from '@core/utils/correlationUtils';
import { getInitialTheme } from '@core/utils/themeUtils';
import { StyleProvider as VanillaExtractStyleProvider, useStyleProvider as useVanillaStyleProvider } from '@soteria/design-system-ve';
import type { Theme } from '@soteria/design-system-ve';
import { LogCategory } from '@soteria/logger/browser';
import { type ReactNode, useEffect } from 'react';

import '@soteria/design-system-ve/style.css';

/**
 * Custom hook that combines the Redux state with the StyleProvider context
 */
export function useStyleProvider() {
  const { theme, setCompleteTheme } = useTheme();
  const vanillaContext = useVanillaStyleProvider();

  // Sync the vanilla-extract theme with Redux when it changes from vanilla-extract
  useEffect(() => {
    if (vanillaContext.theme !== theme) {
      const correlationId = generateCorrelationId();

      logger.debug('Syncing theme from vanilla-extract to Redux', {
        category: LogCategory.SYSTEM,
        operation: 'syncTheme',
        correlationId,
        previousTheme: theme,
        newTheme: vanillaContext.theme,
        source: 'vanilla-extract',
      });

      setCompleteTheme(vanillaContext.theme);
    }
  }, [vanillaContext.theme, theme, setCompleteTheme]);

  // Return a combined context that uses Redux for state but delegates to vanilla-extract for setTheme
  return {
    theme,
    setTheme: (newTheme: Theme) => {
      setCompleteTheme(newTheme);
      vanillaContext.setTheme(newTheme);
    },
  };
}

export function StyleProvider({ children }: { children: ReactNode }) {
  // Get the theme from Redux and hooks to update it
  const { setTheme, setCompleteTheme, setHighContrast } = useTheme();

  // Initialize theme from localStorage on component mount
  useEffect(() => {
    const correlationId = generateCorrelationId();

    // Get theme from localStorage or system preference
    const initialTheme = getInitialTheme();

    logger.debug('Initializing theme from localStorage or system preference', {
      category: LogCategory.SYSTEM,
      operation: 'initializeTheme',
      correlationId,
      initialTheme,
      hasLocalStorage: typeof localStorage !== 'undefined',
      source: localStorage?.getItem('theme') ? 'localStorage' : 'system-preference',
    });

    // Set the complete theme in Redux
    setCompleteTheme(initialTheme);

    // Set the theme name in Redux (light, dark, or system)
    const themeName = initialTheme.includes('hc-') ? (initialTheme.replace('hc-', '') as 'light' | 'dark') : (initialTheme as 'light' | 'dark');

    setTheme(themeName);

    // Set high contrast state in Redux
    const isHighContrast = initialTheme.toString().includes('hc-');

    setHighContrast(isHighContrast);

    logger.debug('Theme initialization completed', {
      category: LogCategory.SYSTEM,
      operation: 'initializeTheme',
      correlationId,
      themeName,
      isHighContrast,
      finalTheme: initialTheme,
    });
  }, [setTheme, setCompleteTheme, setHighContrast]);

  // Get the initial theme (from localStorage or system preference)
  const themeToUse = getInitialTheme();

  return <VanillaExtractStyleProvider defaultTheme={themeToUse}>{children}</VanillaExtractStyleProvider>;
}
